import { Box, styled } from '@mui/material'
import React, { useEffect, useMemo } from 'react'
import { IProjectInfo } from 'src/generated-types/project'
import ConfigurePositionsForm from './ConfigurePositionsForm'
import { useGetPositionsGroupedByDrawingGroups } from 'src/api/projectPosition'
import { useGetSimplePositionDrawings } from 'src/api/position-drawing'
import { DrawingWithGroups, GroupWithPositions } from 'src/generated-types/position-drawing-group'
import { IPosition } from 'src/generated-types'
import { DrawingGroupRow } from 'src/generated-types/configure-positions'
import { ShowDialogProps } from 'src/show-dialog'
import { usePositionChangeTracker } from './usePositionChangeTracker'
import { UseFormReturn } from 'react-hook-form'
import { useRevisionConfigure } from '../../versions/useRevisionConfigure'
import RevisionDialogFooter from '../../versions/RevisionDialogFooter'
import { OlexRequest } from '../../versions/olexMatch.action'
import { IOlexMatchPreview } from 'src/generated-types/olex-match'

interface ConfigurePositionsDialogProps extends ShowDialogProps {
  project: IProjectInfo
  projectForm?: UseFormReturn<any>
  setHasPendingChanges?: React.Dispatch<React.SetStateAction<boolean>>
  fillViewport?: boolean
  // An Olex file the project setup matched to the draft; it opens in the Olex mode.
  olexRequest?: OlexRequest
  onOlexApplied?: (preview: IOlexMatchPreview) => void
}

const ConfigurePositionsDialog: React.FC<ConfigurePositionsDialogProps> = ({
  project,
  pop,
  projectForm,
  setHasPendingChanges,
  fillViewport,
  olexRequest,
  onOlexApplied,
}) => {
  const { data: drawingGroups } = useGetPositionsGroupedByDrawingGroups(
    project.customerId,
    project.id,
    !!project.customerId && !!project.id,
  )
  const revision = useRevisionConfigure(project, { olexRequest, onOlexApplied })

  // Fetch all position drawings to get drawing numbers
  const { data: positionDrawings } = useGetSimplePositionDrawings()
  const { changeTracker, lookups } = usePositionChangeTracker({
    drawingGroups,
    positionDrawings,
  })

  const isUnassignedGroup = (groupName: string) => {
    const normalizedName = groupName.toLowerCase().trim()
    return normalizedName === 'unassigned'
  }

  // Merge server data with pending changes to create unified view
  const { assignedGroups, unassignedPositions } = useMemo(() => {
    if (!drawingGroups) return { assignedGroups: [], unassignedPositions: [] }

    const mergedDrawings: DrawingWithGroups[] = JSON.parse(JSON.stringify(drawingGroups))
    const positionsWithChanges = new Set(changeTracker.changeSet.positionChanges.keys())

    // Remove positions with pending changes from their original locations
    mergedDrawings.forEach(drawing => {
      drawing.groups?.forEach(group => {
        if (group.positions) {
          group.positions = group.positions.filter(pos => !positionsWithChanges.has(pos.id))
        }
      })
    })

    // Helper to find group info from original data
    const findOriginalGroup = (groupId: number): { name: string; projectId: number } | undefined => {
      for (const drawing of drawingGroups || []) {
        for (const group of drawing.groups || []) {
          if (group.id === groupId) {
            return { name: group.name, projectId: group.projectId }
          }
        }
      }
      return undefined
    }

    // Helper to find original position from server data
    const findOriginalPosition = (positionId: number): IPosition | undefined => {
      for (const drawing of drawingGroups || []) {
        for (const group of drawing.groups || []) {
          const pos = group.positions?.find(p => p.id === positionId)
          if (pos) return pos
        }
      }
      return undefined
    }

    // Add pending position changes to their new locations
    changeTracker.changeSet.positionChanges.forEach(change => {
      if (!change.newDrawingId) return

      const drawingNumber =
        change.newDrawingNumber ||
        lookups.drawingIdToNumber.get(change.newDrawingId) ||
        `Drawing ${change.newDrawingId}`

      // Find or create the target drawing
      let targetDrawing = mergedDrawings.find(d => d.drawingId === change.newDrawingId)
      if (!targetDrawing) {
        targetDrawing = {
          drawingId: change.newDrawingId,
          drawingNumber,
          groups: [],
        }
        mergedDrawings.push(targetDrawing)
      }
      // Find original position to preserve properties like 'type'
      const originalPosition = findOriginalPosition(change.positionId)

      // Create position object with original data merged in
      const position: IPosition = {
        ...originalPosition,
        id: change.positionId,
        name: change.positionName,
        positionDrawingId: change.newDrawingId,
      } as IPosition

      // Determine target group
      if (change.newGroupName) {
        // New group or existing group by name
        let targetGroup = targetDrawing.groups?.find(g => g.name === change.newGroupName)
        if (!targetGroup) {
          const newGroup: GroupWithPositions = {
            id: -Date.now(),
            name: change.newGroupName,
            positionDrawingId: change.newDrawingId,
            projectId: project.id as number,
            positions: [],
          }
          if (!targetDrawing.groups) targetDrawing.groups = []
          targetDrawing.groups.push(newGroup)
          targetGroup = newGroup
        }
        if (!targetGroup.positions) targetGroup.positions = []
        targetGroup.positions.push(position)
      } else if (change.newGroupId && change.newGroupId > 0) {
        // Existing group by ID - may need to move to new drawing
        let targetGroup = targetDrawing.groups?.find(g => g.id === change.newGroupId)
        if (!targetGroup) {
          // Group doesn't exist in this drawing yet (drawing changed for group)
          // Create a representation of the group in the new drawing
          const originalGroup = findOriginalGroup(change.newGroupId)
          if (originalGroup) {
            const movedGroup: GroupWithPositions = {
              id: change.newGroupId,
              name: originalGroup.name,
              positionDrawingId: change.newDrawingId,
              projectId: originalGroup.projectId,
              positions: [],
            }
            if (!targetDrawing.groups) targetDrawing.groups = []
            targetDrawing.groups.push(movedGroup)
            targetGroup = movedGroup
          }
        }
        if (targetGroup) {
          if (!targetGroup.positions) targetGroup.positions = []
          targetGroup.positions.push(position)
        }
      } else {
        // Unassigned (no group)
        let unassignedGroup = targetDrawing.groups?.find(g => isUnassignedGroup(g.name))
        if (!unassignedGroup) {
          const newUnassigned: GroupWithPositions = {
            id: -Date.now() - 1,
            name: 'Unassigned',
            positionDrawingId: change.newDrawingId,
            projectId: project.id as number,
            positions: [],
          }
          if (!targetDrawing.groups) targetDrawing.groups = []
          targetDrawing.groups.push(newUnassigned)
          unassignedGroup = newUnassigned
        }
        if (!unassignedGroup.positions) unassignedGroup.positions = []
        unassignedGroup.positions.push(position)
      }
    })

    // Now separate into assigned and unassigned
    const assigned: DrawingGroupRow[] = []
    const unassigned: DrawingGroupRow[] = []

    mergedDrawings.forEach(drawing => {
      if (!drawing.groups) return
      drawing.groups.forEach(group => {
        // Skip empty groups
        if (!group.positions || group.positions.length === 0) return
        const isPending = group.id < 0 || group.positions.some(p => positionsWithChanges.has(p.id))

        if (isUnassignedGroup(group.name)) {
          unassigned.push({
            drawingNumber: drawing.drawingNumber,
            group,
            drawingId: drawing.drawingId,
            isPending,
          })
        } else {
          assigned.push({
            drawingNumber: drawing.drawingNumber,
            group,
            drawingId: drawing.drawingId,
            isPending,
          })
        }
      })
    })

    return { assignedGroups: assigned, unassignedPositions: unassigned }
  }, [drawingGroups, changeTracker.changeSet, lookups, project.id])

  useEffect(() => {
    setHasPendingChanges?.(false)
  }, [setHasPendingChanges])

  return (
    <DialogBody fillViewport={fillViewport}>
      <ConfigurePositionsForm
        customerId={project.customerId}
        projectId={project.id}
        localityId={project.localityId}
        unassignedGroups={unassignedPositions}
        assignedGroups={assignedGroups}
        lookups={lookups}
        projectForm={projectForm}
        fill={fillViewport}
        revision={revision}
      />
      {revision && <RevisionDialogFooter revision={revision} onClose={pop} />}
    </DialogBody>
  )
}

export default ConfigurePositionsDialog

const DialogBody = styled(Box, { shouldForwardProp: prop => prop !== 'fillViewport' })<{ fillViewport?: boolean }>(
  ({ fillViewport }) =>
    fillViewport ? { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' } : {},
)
